import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/** A proforma is a SalesOrder with documentType 'proforma' (db-sales.js). */
export interface ProformaLine {
  productId?: string | null;
  description?: string;
  hsnSac?: string;
  unit?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
  quantity: number;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percent';
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  lineTotal?: number;
}

export interface Proforma {
  _id: string;
  documentNumber?: string;
  orderNumber?: string;
  date: string;
  documentType: string;
  status: string;
  customerId?: { _id: string; name: string; email?: string; phone?: string } | string | null;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  lines: ProformaLine[];
  subTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  notes?: string;
  crmDealId?: string | null;
  crmLeadId?: string | null;
  dueDate?: string | null;
  shippingAddress?: Record<string, unknown> | null;
  billingAddress?: Record<string, unknown> | null;
  shippingCharge?: number;
  freightCharges?: number;
  createdBy?: { display_name?: string } | string | null;
  lostReasonId?: { _id: string; name: string } | string | null;
  lostReasonName?: string;
  lostReasonNote?: string;
  lostAt?: string | null;
  customerPoNumber?: string;
  customerPoDate?: string | null;
}

const KEY = ['proforma'];

export type ProformaSort = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc' | 'customer_asc' | 'customer_desc';

export interface ProformaListParams {
  /** A single status, 'all', or a comma-separated list (e.g. from the
   *  dashboard's Open Proforma tile) — the backend accepts both. */
  status?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  sort?: ProformaSort;
  /** Employee-wise filter — only honored server-side for superadmin/department_admin. */
  createdBy?: string;
  page?: number;
  limit?: number;
}

export interface ProformaListResult {
  data: Proforma[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
  stats?: { totalValue: number; openCount: number };
}

/**
 * Paginated + server-filtered list, same shape and same backend route as
 * Quotations' useQuotationsPaged (GET /api/sales-ops/orders already supports
 * all of this for every documentType) — brought up to parity so Proforma has
 * the same search/status/date-range/pagination Quotations already had.
 */
export function useProformaList(params: ProformaListParams = {}) {
  const { status, search, fromDate, toDate, sort, createdBy, page = 1, limit = 50 } = params;
  return useQuery({
    queryKey: [...KEY, 'paged', { status, search, fromDate, toDate, sort, createdBy, page, limit }],
    queryFn: async () => {
      const p = new URLSearchParams({
        documentType: 'proforma',
        compact: 'true',
        page: String(page),
        limit: String(limit),
      });
      if (status && status !== 'all') p.set('status', status);
      if (search?.trim()) p.set('search', search.trim());
      if (fromDate) p.set('fromDate', fromDate);
      if (toDate) p.set('toDate', toDate);
      if (sort) p.set('sort', sort);
      if (createdBy) p.set('createdBy', createdBy);
      const { data } = await api.get<ProformaListResult>(`/api/sales-ops/orders?${p}`);
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface CreateProformaPayload {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  lines: ProformaLine[];
}

export function useProformaMutations() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: KEY }),
    qc.invalidateQueries({ queryKey: ['invoice-requests'] }),
    qc.invalidateQueries({ queryKey: ['pipeline'] }),
    qc.invalidateQueries({ queryKey: ['deals'] }),
    qc.invalidateQueries({ queryKey: ['leads'] }),
  ]);

  const create = useMutation({
    mutationFn: async (payload: CreateProformaPayload) => {
      const { data } = await api.post('/api/sales-ops/orders', {
        ...payload,
        documentType: 'proforma',
        date: new Date().toISOString(),
      });
      return data as Proforma;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CreateProformaPayload }) => {
      const { data } = await api.put(`/api/sales-ops/orders/${id}`, payload);
      return data as Proforma;
    },
    onSuccess: invalidate,
  });

  /**
   * Confirm a proforma — allocates its real document number (numbering-
   * allocator.js, documentType 'proforma') and locks it from further edits.
   */
  const confirm = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/sales-ops/orders/${id}/confirm`)).data as Proforma,
    onSuccess: invalidate,
  });

  /**
   * Convert a confirmed proforma into an Invoice Request — same bridge
   * Quotations uses (useQuotations.ts) so Accounts approves it the same way
   * regardless of which document it started from.
   */
  const convertToInvoiceRequest = useMutation({
    mutationFn: async ({ proforma, productNames }: {
      proforma: Proforma;
      productNames: Record<string, { name: string; hsnSac?: string }>;
    }) => {
      const items = proforma.lines.map((l) => {
        const meta = productNames[String(l.productId)] || { name: l.description || 'Service' };
        const taxRate = (l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0);
        return {
          catalogItemId: l.productId || null,
          name: meta.name || l.description || 'Service',
          description: l.description || '',
          notes: (l as { notes?: string }).notes || '',
          hsnSac: meta.hsnSac || '',
          unit: l.unit || '',
          packSize: l.packSize || '',
          qtyPerPack: Number(l.qtyPerPack) || 0,
          totalPackSize: l.totalPackSize || '',
          volumeLiters: Number(l.volumeLiters) || 0,
          quantity: l.quantity,
          rate: l.unitPrice,
          discountPercent: l.discountType === 'percent' ? (l.discount || 0) : 0,
          taxRate,
        };
      });

      const { data } = await api.post('/api/invoice-requests', {
        clientId: typeof proforma.customerId === 'object' ? proforma.customerId?._id : proforma.customerId,
        clientName: proforma.customerName || '',
        sourceQuotationId: proforma._id,
        sourceQuotationNumber: proforma.orderNumber || proforma.documentNumber || '',
        sourceDealId: proforma.crmDealId || null,
        items,
        notes: proforma.notes || '',
        dueDate: proforma.dueDate || null,
        shippingAddress: proforma.shippingAddress || null,
        billingAddress: proforma.billingAddress || null,
        shippingCharge: proforma.shippingCharge ?? proforma.freightCharges ?? 0,
      });
      return data as { id: string; number: string };
    },
    onSuccess: invalidate,
  });

  const markLost = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: { lostReasonId: string; lostReasonNote?: string; closeDeal?: boolean } }) =>
      (await api.post(`/api/sales-ops/orders/${id}/mark-lost`, payload)).data,
    onSuccess: invalidate,
  });

  return { create, update, confirm, markLost, convertToInvoiceRequest };
}
